import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import formatLastActive from '../../utils/formatLastActive';
import './Admin.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
    storage_limit_gb: '',
  });

  const loadUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetForm = () => {
    setFormData({ username: '', email: '', password: '', is_admin: false, storage_limit_gb: '' });
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createUser(formData);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (formData.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;
      if (formData.is_admin !== undefined) updateData.is_admin = formData.is_admin;
      if (formData.storage_limit_gb !== '') {
        updateData.storage_limit_gb = parseFloat(formData.storage_limit_gb) || 0;
      }

      await adminAPI.updateUser(editingUser.username, updateData);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      await adminAPI.deleteUser(username);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    if (user.is_active && !window.confirm(
      `Disable "${user.username}"? They won't be able to log in. Their files and transcripts are kept ` +
      `(media is still deleted after 30 days of inactivity). You can re-enable them any time.`
    )) {
      return;
    }

    try {
      await adminAPI.updateUser(user.username, { is_active: !user.is_active });
      setError(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handlePurgeContent = async (username) => {
    if (!window.confirm(`Delete ALL files and transcripts for "${username}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminAPI.purgeUserContent(username);
      setError(null);
      loadUsers();
      alert(`Purged: ${response.data.transcripts_deleted} transcript(s) and ${response.data.files_deleted} file(s) removed.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to purge content');
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      is_admin: user.is_admin,
      storage_limit_gb: user.storage_limit_gb || '',
    });
    setShowCreateForm(false);
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)} className="alert-close">&times;</button>
        </div>
      )}

      <div className="user-actions">
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowCreateForm(true);
          }}
        >
          Create User
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingUser) && (
        <div className="user-form card">
          <h3>{editingUser ? `Edit User: ${editingUser.username}` : 'Create New User'}</h3>
          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
            {!editingUser && (
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  minLength={3}
                  pattern="^[a-zA-Z0-9_.@]+$"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required={!editingUser}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Password {editingUser && '(leave blank to keep current)'}
              </label>
              <input
                type="password"
                className="form-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                Admin privileges
              </label>
            </div>

            {editingUser && (
              <div className="form-group">
                <label className="form-label">
                  Storage Limit (GB) - leave blank or 0 for default (5GB)
                </label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.storage_limit_gb}
                  onChange={(e) => setFormData({ ...formData, storage_limit_gb: e.target.value })}
                  min="0"
                  step="0.5"
                  placeholder="5"
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Administrators */}
      <div className="user-list">
        <h3 className="user-group-title">Administrators</h3>
        <table className="user-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Storage</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.filter(u => u.is_admin).map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  <div className="storage-cell">
                    <div className="storage-bar-mini">
                      <div
                        className={`storage-bar-fill ${user.storage?.percentage >= 90 ? 'critical' : user.storage?.percentage >= 75 ? 'warning' : ''}`}
                        style={{ width: `${user.storage?.percentage || 0}%` }}
                      />
                    </div>
                    <span className="storage-text-small">
                      {user.storage?.used_formatted} / {user.storage?.limit_formatted}
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'status-completed' : 'status-failed'}`}>
                    {user.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <span title={user.last_active_at ? new Date(user.last_active_at).toLocaleString() : ''}>
                    {formatLastActive(user.last_active_at)}
                  </span>
                  {user.media_warning_sent_at && (
                    <div
                      className="storage-text-small media-warning-note"
                      title={`Media deletion warning emailed ${new Date(user.media_warning_sent_at).toLocaleString()}. Cancelled automatically if they log in.`}
                    >
                      Deletion warning sent
                    </div>
                  )}
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => startEdit(user)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleToggleActive(user)}
                      title={user.is_active ? 'Block login without deleting anything' : 'Allow this user to log in again'}
                    >
                      {user.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handlePurgeContent(user.username)}
                      title="Delete all files and transcripts"
                    >
                      Purge Content
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteUser(user.username)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Regular Users */}
      <div className="user-list">
        <h3 className="user-group-title">Users</h3>
        <table className="user-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Storage</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.filter(u => !u.is_admin).length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  No regular users yet
                </td>
              </tr>
            ) : (
              users.filter(u => !u.is_admin).map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <div className="storage-cell">
                      <div className="storage-bar-mini">
                        <div
                          className={`storage-bar-fill ${user.storage?.percentage >= 90 ? 'critical' : user.storage?.percentage >= 75 ? 'warning' : ''}`}
                          style={{ width: `${user.storage?.percentage || 0}%` }}
                        />
                      </div>
                      <span className="storage-text-small">
                        {user.storage?.used_formatted} / {user.storage?.limit_formatted}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'status-completed' : 'status-failed'}`}>
                      {user.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <span title={user.last_active_at ? new Date(user.last_active_at).toLocaleString() : ''}>
                      {formatLastActive(user.last_active_at)}
                    </span>
                    {user.media_warning_sent_at && (
                      <div
                        className="storage-text-small media-warning-note"
                        title={`Media deletion warning emailed ${new Date(user.media_warning_sent_at).toLocaleString()}. Cancelled automatically if they log in.`}
                      >
                        Deletion warning sent
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => startEdit(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggleActive(user)}
                        title={user.is_active ? 'Block login without deleting anything' : 'Allow this user to log in again'}
                      >
                        {user.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handlePurgeContent(user.username)}
                        title="Delete all files and transcripts"
                      >
                        Purge Content
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteUser(user.username)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserManagement;
